"use client";
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';
import { useDebug } from '@/utils/useDebugLog';
import { useViewSize } from '@/providers/ViewSizeContext';

import className from 'classnames/bind';
import styles from './RevealWrapper.module.scss';
const cx = className.bind(styles);

interface RevealWrapperProps  extends React.PropsWithChildren {
  className?: string;
  isInView?: boolean;
  isEnabled?: boolean;
  debug?: boolean;
}

const RevealWrapper = forwardRef<HTMLDivElement, RevealWrapperProps>(({ children, className, isInView, isEnabled = true, debug = false }, ref) => {
  const { log } = useDebug({ name: 'RevealWrapper', bgColor: '#2d9cdb', textColor: '#ffffff', enabled: debug });
  const { isDesktop, viewWidth } = useViewSize();

  const REVEAL = AnimVals;
  
  const getAnimValues = useCallback((desktop: boolean) => ({
    distance: desktop ? REVEAL.lg.distance : REVEAL.sm.distance,
    duration: desktop ? REVEAL.lg.duration : REVEAL.sm.duration,
    ease: desktop ? REVEAL.lg.ease : REVEAL.sm.ease
  }), []);

  const [animValues, setAnimValues] = useState(() => getAnimValues(false));

  const motionProps = useMemo(() => {
    const { distance, duration, ease } = animValues;
    return {
      initial: {
        opacity: REVEAL.opacity,
        y: distance
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration,
        ease
      }
    };
  }, [animValues]);

  useEffect(() => {
    setAnimValues(getAnimValues(isDesktop));
  }, [isDesktop, getAnimValues]);

  log('render', { isDesktop, viewWidth, isInView, isEnabled, motionProps });

  if (!isEnabled) {
    log('animations disabled - rendering static container');
    return (
      <div className={cx(["reveal-wrapper", className])} ref={ref}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      className={cx(["reveal-wrapper", className])}
      initial={motionProps.initial}
      animate={isInView ? motionProps.animate : motionProps.initial}
      transition={motionProps.transition}
    >
      {children}
    </motion.div>
  );
});

export default RevealWrapper;
